/**
 * Task #2111 — Badge tri-stato condiviso per SIP trunk + extension.
 *
 * Variants visivi:
 *   - healthy  → verde, "Registrato"
 *   - degraded → giallo, "Degraded" (gateway DOWN ma traffico CDR recente)
 *   - failed   → rosso, "Fallito"
 *   - unknown  → grigio, "Sconosciuto" (mai osservato dal monitor)
 *
 * Strict union (no string magic), exhaustive switch. Tooltip ricco
 * opzionale (reason, last_call_at, registration_state).
 */
import * as React from 'react';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

export type SipHealthBadgeStatus = 'healthy' | 'degraded' | 'failed' | 'unknown';
export type SipHealthBadgeAuthMode = 'register' | 'ip_auth';

export interface SipHealthBadgeProps {
  status: SipHealthBadgeStatus;
  authMode?: SipHealthBadgeAuthMode | null;
  reason?: string | null;
  lastCallAt?: string | null;
  registrationState?: 'registered' | 'trying' | 'failed' | 'unregistered' | 'unknown' | null;
  source?: 'esl' | 'db' | null;
  className?: string;
  compact?: boolean;
}

interface Variant {
  label: string;
  glyph: string;
  classes: string;
  defaultReason: string;
}

const VARIANTS_REGISTER: Record<SipHealthBadgeStatus, Variant> = {
  healthy: {
    label: 'Registrato',
    glyph: '✓',
    classes: 'bg-green-500/20 text-green-600 dark:text-green-300 ring-1 ring-green-500/30',
    defaultReason: 'Gateway registrato e signaling nominale.',
  },
  degraded: {
    label: 'Degraded',
    glyph: '◐',
    classes: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-300 ring-1 ring-yellow-500/30',
    defaultReason: 'REGISTER intermittente, traffico CDR presente.',
  },
  failed: {
    label: 'Fallito',
    glyph: '✗',
    classes: 'bg-red-500/20 text-red-600 dark:text-red-300 ring-1 ring-red-500/30',
    defaultReason: 'Gateway DOWN e nessuna chiamata negli ultimi 5 minuti.',
  },
  unknown: {
    label: 'Sconosciuto',
    glyph: '?',
    classes: 'bg-muted text-muted-foreground ring-1 ring-border',
    defaultReason: 'Stato non ancora osservato dal monitor.',
  },
};

const VARIANTS_IP_AUTH: Record<SipHealthBadgeStatus, Variant> = {
  healthy: {
    label: 'IP-Auth attivo',
    glyph: '✓',
    classes: 'bg-green-500/20 text-green-600 dark:text-green-300 ring-1 ring-green-500/30',
    defaultReason: 'Trunk IP-auth: nessun REGISTER per design (whitelist IP sul SBC).',
  },
  degraded: {
    label: 'IP-Auth degradato',
    glyph: '◐',
    classes: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-300 ring-1 ring-yellow-500/30',
    defaultReason: 'Trunk IP-auth: probe Sofia disallineato vs traffico reale via SBC.',
  },
  failed: {
    label: 'Fallito',
    glyph: '✗',
    classes: 'bg-red-500/20 text-red-600 dark:text-red-300 ring-1 ring-red-500/30',
    defaultReason: 'Trunk IP-auth: nessuna chiamata recente e probe SBC negativo.',
  },
  unknown: {
    label: 'Sconosciuto',
    glyph: '?',
    classes: 'bg-muted text-muted-foreground ring-1 ring-border',
    defaultReason: 'Stato non ancora osservato dal monitor.',
  },
};

function formatLastCall(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return null;
  }
}

export function SipHealthBadge(props: SipHealthBadgeProps): React.ReactElement {
  const { status, authMode, reason, lastCallAt, registrationState, source, className, compact } = props;
  const variants = authMode === 'ip_auth' ? VARIANTS_IP_AUTH : VARIANTS_REGISTER;
  const v = variants[status];
  const lastCallFmt = formatLastCall(lastCallAt);

  const tooltipLines: string[] = [reason && reason.trim().length > 0 ? reason : v.defaultReason];
  if (registrationState) tooltipLines.push(`Signaling: ${registrationState}`);
  if (lastCallFmt) tooltipLines.push(`Ultima chiamata: ${lastCallFmt}`);
  if (source === 'db') tooltipLines.push('Dato da DB (ESL non disponibile)');
  const title = tooltipLines.join(' · ');

  return (
    <Badge
      variant="outline"
      title={title}
      className={cn(
        'border-0 shadow-sm backdrop-blur-md',
        compact ? 'text-[10px] h-5 px-2' : '',
        v.classes,
        className,
      )}
    >
      <span aria-hidden="true">{v.glyph}</span>
      <span className="ml-1">{v.label}</span>
    </Badge>
  );
}
